import { useState } from 'react';

import { Button } from '../../components/Button';
import { CardGrid } from '../../components/CardGrid';
import { ContentArea } from '../../components/ContentArea';
import { EmptyMessage } from '../../components/EmptyMessage';
import { ItemCard } from '../../components/ItemCard';
import { Modal } from '../../components/Modal';
import { Section, Title, TitleRow } from '../../components/Section';
import { SkeletonCard } from '../../components/SkeletonCard';
import { ChevronLeftIcon } from '../../icons/ChevronLeftIcon';
import { ChevronRightIcon } from '../../icons/ChevronRightIcon';
import { CloseIcon } from '../../icons/CloseIcon';
import {
  DetailActionBar,
  DetailCarouselControls,
  DetailClose,
  DetailContent,
  DetailDescription,
  DetailDot,
  DetailDots,
  DetailHeading,
  DetailImageSlot,
  DetailImageWrapper,
  DetailLabel,
  DetailMenus,
  DetailNav,
  DetailNavButton,
  DetailPlaceholder,
  DetailSection,
  DetailSectionHeading,
  DetailSlide,
  DetailSlideImg,
  DetailTitle,
  DetailTrack,
  OpeningDayName,
  OpeningDayTime,
  OpeningHoursDay,
  OpeningHoursRow,
  PillButton,
  TodayBadge,
} from './DiningListTemplate.styles';
import type { DiningListItem, DiningListTemplateProps } from './DiningListTemplate.types';

const OPENING_HOURS_PREVIEW_COUNT = 3;

export function DiningListTemplate({
  items,
  onSelectItem,
  onEditSearch,
  isLoading = false,
  columns = 2,
  imageAspectRatio,
  imageMobileAspectRatio,
  labels,
}: DiningListTemplateProps) {
  const [detailId, setDetailId] = useState<string | null>(null);
  const [showAllHours, setShowAllHours] = useState(false);
  const [imageIndex, setImageIndex] = useState(0);
  const activeItem = items.find((item) => item.id === detailId) ?? null;

  const openDetail = (id: string) => {
    setShowAllHours(false);
    setImageIndex(0);
    setDetailId(id);
  };
  const closeDetail = () => setDetailId(null);

  const images = activeItem?.imageUrls ?? [];
  const safeImageIndex = Math.min(imageIndex, Math.max(images.length - 1, 0));
  const goToImage = (index: number) => setImageIndex((index + images.length) % images.length);

  const openingHours = activeItem?.openingHours ?? [];
  const hasMoreHours = openingHours.length > OPENING_HOURS_PREVIEW_COUNT;
  const visibleHours =
    hasMoreHours && !showAllHours
      ? openingHours.slice(0, OPENING_HOURS_PREVIEW_COUNT)
      : openingHours;

  const renderCard = (item: DiningListItem) => (
    <ItemCard
      key={item.id}
      image={{ src: item.imageUrls?.[0], alt: item.name, placeholderLabel: labels.noImage }}
      imageAspectRatio={imageAspectRatio}
      imageMobileAspectRatio={imageMobileAspectRatio}
      title={item.name}
      meta={item.label || undefined}
      imageAction={{ label: labels.quickView, onClick: () => openDetail(item.id) }}
      unavailable={item.unavailable}
      unavailableLabel={item.unavailable ? labels.unavailable : undefined}
      editSearchLabel={labels.editSearch}
      onEditSearch={onEditSearch}
      ariaLabel={item.name}
      onSelect={() => onSelectItem(item.id)}
    />
  );

  return (
    <Section>
      <TitleRow>
        <Title>{labels.title}</Title>
      </TitleRow>

      {isLoading && items.length === 0 ? (
        <CardGrid $columns={columns}>
          {Array.from({ length: 4 }).map((_, index) => (
            <SkeletonCard
              key={index}
              variant="grid"
              aspectRatio={imageAspectRatio}
              mobileAspectRatio={imageMobileAspectRatio}
            />
          ))}
        </CardGrid>
      ) : items.length === 0 ? (
        <EmptyMessage>{labels.noResults}</EmptyMessage>
      ) : (
        <ContentArea>
          <CardGrid $columns={columns}>{items.map(renderCard)}</CardGrid>
        </ContentArea>
      )}

      <Modal
        open={activeItem !== null}
        onClose={closeDetail}
        ariaLabel={activeItem?.name}
        labels={{ close: labels.close }}
        disableBodyPadding
        size="lg"
        footer={
          activeItem ? (
            <DetailActionBar>
              <Button
                type="button"
                variant="primary"
                size="large"
                fullWidth
                onClick={() => {
                  onSelectItem(activeItem.id);
                  closeDetail();
                }}
              >
                {labels.reserve}
              </Button>
            </DetailActionBar>
          ) : undefined
        }
      >
        {activeItem && (
          <>
            <DetailImageSlot>
              <DetailImageWrapper>
                {images.length > 0 ? (
                  <DetailTrack $slideIndex={safeImageIndex}>
                    {images.map((src, index) => (
                      <DetailSlide key={`${index}-${src}`} aria-hidden={index !== safeImageIndex}>
                        <DetailSlideImg src={src} alt={activeItem.name} />
                      </DetailSlide>
                    ))}
                  </DetailTrack>
                ) : (
                  <DetailPlaceholder>{labels.noImage}</DetailPlaceholder>
                )}

                <DetailClose
                  type="button"
                  onClick={closeDetail}
                  aria-label={labels.close ?? 'Close'}
                >
                  <CloseIcon size={16} />
                </DetailClose>

                {images.length > 1 && (
                  <DetailCarouselControls>
                    <DetailDots>
                      {images.map((src, index) => (
                        <DetailDot
                          key={`${index}-${src}`}
                          type="button"
                          data-active={index === safeImageIndex}
                          aria-current={index === safeImageIndex}
                          aria-label={`${index + 1}`}
                          onClick={() => goToImage(index)}
                        />
                      ))}
                    </DetailDots>
                    <DetailNav>
                      <DetailNavButton
                        type="button"
                        aria-label={labels.previousImage ?? 'Previous image'}
                        onClick={() => goToImage(safeImageIndex - 1)}
                      >
                        <ChevronLeftIcon size={24} />
                      </DetailNavButton>
                      <DetailNavButton
                        type="button"
                        aria-label={labels.nextImage ?? 'Next image'}
                        onClick={() => goToImage(safeImageIndex + 1)}
                      >
                        <ChevronRightIcon size={24} />
                      </DetailNavButton>
                    </DetailNav>
                  </DetailCarouselControls>
                )}
              </DetailImageWrapper>
            </DetailImageSlot>

            <DetailContent>
              <DetailHeading>
                <DetailTitle>{activeItem.name}</DetailTitle>
                {activeItem.label && activeItem.label !== activeItem.description && (
                  <DetailLabel>{activeItem.label}</DetailLabel>
                )}
                {activeItem.description && (
                  <DetailDescription>{activeItem.description}</DetailDescription>
                )}
              </DetailHeading>

              {openingHours.length > 0 && (
                <DetailSection>
                  <DetailSectionHeading>
                    {labels.openingHours ?? 'Opening hours'}
                  </DetailSectionHeading>
                  {visibleHours.map((entry) => (
                    <OpeningHoursRow key={entry.day}>
                      <OpeningHoursDay>
                        <OpeningDayName>{entry.day}</OpeningDayName>
                        <OpeningDayTime>{entry.hours}</OpeningDayTime>
                      </OpeningHoursDay>
                      {entry.isToday && labels.today && <TodayBadge>{labels.today}</TodayBadge>}
                    </OpeningHoursRow>
                  ))}
                  {hasMoreHours && (
                    <PillButton
                      as="button"
                      type="button"
                      onClick={() => setShowAllHours((v) => !v)}
                    >
                      {showAllHours
                        ? labels.showLess ?? 'Show less'
                        : labels.showMore ?? 'Show more'}
                    </PillButton>
                  )}
                </DetailSection>
              )}

              {activeItem.menus && activeItem.menus.length > 0 && (
                <DetailSection>
                  <DetailSectionHeading>{labels.menus ?? 'Menus'}</DetailSectionHeading>
                  <DetailMenus>
                    {activeItem.menus.map((menu) => (
                      <PillButton
                        key={menu.id}
                        href={menu.url}
                        target="_blank"
                        rel="noreferrer"
                        aria-label={`${labels.viewMenu}: ${menu.title}`}
                      >
                        {menu.title}
                      </PillButton>
                    ))}
                  </DetailMenus>
                </DetailSection>
              )}
            </DetailContent>
          </>
        )}
      </Modal>
    </Section>
  );
}
